import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocale } from '@/i18n/locale'
import { NavLink } from '@/components/NavLink'
import { track } from '@/lib/analytics'
import './audience.css'

/**
 * Блок 2 «Проблема и кому» (ТЗ 4.2). Стоит вплотную перед #tracks — мандат
 * «после «кому» — сразу ЧТО покупаешь», поэтому после сегментов и строки
 * регионов ничего нет: страж section-order меряет зазор до #tracks.
 *
 * Три фразы верха — слова из цитат-болей бывшей секции PainMirror (все семь
 * живут на /vnedrenie-ii-agentov). Строка-переход — её же «Наш ответ».
 * Аудит 2026-09-21 назвал эти три фразы лучшим местом страницы: текст не
 * трогаем, уплотнили только отступы.
 *
 * Низ сжат тем же аудитом (план сокращения, шаг C): вместо H3, абзаца и
 * четырёх карточек — две колонки «кому подходит / кому нет» и плотная
 * строка сегментов. Факты — из прежнего абзаца, новых цифр нет; описания
 * сегментов живут на страницах, куда ведут ссылки. Сектора и строка про
 * холдинг раньше стояли ещё и в #tracks — теперь только здесь.
 * Ссылки сегментов и регионов держат стражи home-links и sitemap-inbound.
 */

type Pain = { tag: string; big: string; sub: string }
type Segment = { sector: string; chip: string; title: string; href: string }
type Region = { label: string; href: string }

const COPY = {
  ru: {
    srPains: 'С чем приходят собственники',
    pains: [
      {
        tag: 'Деньги',
        big: '«Не знаю, где деньги»',
        sub: '«Выручка есть — прибыли не вижу. Живу ли я на кредит?»',
      },
      {
        tag: 'Зависимость от собственника',
        big: 'Бизнес живёт в вашей голове',
        sub: '«Эта информация находится только у меня и во мне». Делегировать — займёт год.',
      },
      {
        tag: 'Управляемость',
        big: 'Решения принимаются по ощущениям',
        sub: 'Потому что достать цифру дольше, чем решить наугад.',
      },
    ] as Pain[],
    bridge:
      'Мы выносим бизнес из вашей головы и голов вашей команды в общую систему компании: учёт, клиенты, решения.',
    eyebrow: 'Компания по внедрению ИИ в бизнес · для кого',
    fitLabel: 'Кому подходит',
    fit:
      'Среднему бизнесу с командой и оборотом от 300 млн ₽ в год, в любой нише, B2B и B2C: там, где экономия исчисляется десятками миллионов и идёт прямо в карман собственнику. Подтверждённые сектора — транспорт, энергетика и тендеры, девелопмент, финтех, e‑commerce.',
    unfitLabel: 'Кому не подойдёт',
    unfit:
      'При небольшом потоке операций готовые инструменты чаще выгоднее заказного внедрения — честно скажем об этом на бесплатной диагностике.',
    segments: [
      { sector: 'manufacturing', chip: 'учёт · тендеры', title: 'Производство', href: '/vnedrenie-ii-agentov' },
      { sector: 'distribution', chip: 'CRM · продажи', title: 'Дистрибуция и торговля', href: '/services/ai-crm' },
      { sector: 'services', chip: 'время собственника', title: 'Услуги с командой от 50 человек', href: '/digital/assistent' },
      { sector: 'holding', chip: 'своё ИИ‑ядро', title: 'Холдинги 5+ юрлиц', href: '/vnedrenie-ii-agentov#holding' },
    ] as Segment[],
    regionsLead: 'Работаем по всей России и СНГ:',
    regions: [
      { label: 'Внедрение ИИ в Москве', href: '/regions/moskva' },
      { label: 'в Санкт-Петербурге', href: '/regions/spb' },
      { label: 'в Казани', href: '/regions/kazan' },
      { label: 'в Новосибирске', href: '/regions/novosibirsk' },
    ] as Region[],
  },
  en: {
    srPains: 'What owners come to us with',
    pains: [
      {
        tag: 'Money',
        big: '“I don’t know where the money is”',
        sub: '“There’s revenue — I don’t see profit. Am I living on credit?”',
      },
      {
        tag: 'Owner dependency',
        big: 'The business lives in your head',
        sub: '“This information exists only in me.” Delegating would take a year.',
      },
      {
        tag: 'Control',
        big: 'Decisions are made by gut feel',
        sub: 'Because getting the number takes longer than guessing.',
      },
    ] as Pain[],
    bridge:
      'We move the business out of your head — and out of your team’s heads — into one company-wide system: accounting, clients, decisions.',
    eyebrow: 'Who this is for',
    fitLabel: 'A good fit',
    fit:
      'Mid-sized companies with a team and revenue from $3M a year, in any industry, B2B and B2C — where the savings run to six figures a year and go straight to the owner. Proven sectors: transportation, energy and tenders, real estate development, fintech, e‑commerce.',
    unfitLabel: 'Not a fit',
    unfit:
      'With a small flow of operations, off-the-shelf tools usually beat a custom implementation — we will say so honestly at the free diagnostics.',
    // Внутренние страницы ещё не переведены — на /en сегменты без ссылок.
    segments: [
      { sector: 'manufacturing', chip: 'accounting · tenders', title: 'Manufacturing', href: '' },
      { sector: 'distribution', chip: 'CRM · sales', title: 'Distribution and trade', href: '' },
      { sector: 'services', chip: 'owner’s time', title: 'Services with a team of 50+', href: '' },
      { sector: 'holding', chip: 'own AI core', title: 'Holdings with 5+ legal entities', href: '' },
    ] as Segment[],
    regionsLead: '',
    regions: [] as Region[],
  },
}

// На сервере useLayoutEffect только шумит предупреждением; в браузере он нужен,
// чтобы спрятать фразы ДО первой отрисовки после гидратации — без мигания.
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const LABEL = 'font-mono text-small uppercase tracking-wider'

export function Audience() {
  const locale = useLocale()
  const t = COPY[locale]
  const isEn = locale === 'en'
  const painsRef = useRef<HTMLOListElement>(null)

  useIsoLayoutEffect(() => {
    const root = painsRef.current
    if (!root || !('IntersectionObserver' in window)) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    // Классы ставим прямо в DOM, а не через state: SSR-разметка остаётся
    // полностью видимой (поисковик, no-JS), и гидратация не видит расхождений.
    // Прячем только то, что ещё ниже экрана, — уже видимое не моргает.
    const items = Array.from(root.querySelectorAll<HTMLElement>('.aud-pain'))
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue
          e.target.classList.remove('aud-armed')
          io.unobserve(e.target)
        }
      },
      { threshold: 0.3 },
    )
    for (const el of items) {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.9) continue
      el.classList.add('aud-armed')
      io.observe(el)
    }
    return () => {
      io.disconnect()
      items.forEach((el) => el.classList.remove('aud-armed'))
    }
  }, [])

  return (
    <section id="audience" data-block="audience" className="section scroll-mt-16">
      <div className="container-x">
        {/* Верх: проблема тремя утверждениями. Отступы между фразами
            уплотнены (аудит 2026-09-21): блок был 2,1 экрана на десктопе. */}
        <ol ref={painsRef} aria-label={t.srPains} className="space-y-7 sm:space-y-9">
          {t.pains.map((p, i) => (
            <li key={p.tag} className="aud-pain grid grid-cols-1 lg:grid-cols-12 gap-x-8 gap-y-3">
              <div className={`aud-tag lg:col-span-3 xl:col-span-2 lg:pt-5 ${LABEL} text-muted flex items-baseline gap-3`}>
                <span className="aud-dot" aria-hidden />
                {/* На телефоне метка в одну строку, на широком — номер над темой:
                    длинная тема иначе рвётся посреди «03 /». */}
                <span className="flex flex-wrap gap-x-2 lg:flex-col lg:gap-y-1">
                  <span className="text-ink">0{i + 1}</span>
                  <span>{p.tag}</span>
                </span>
              </div>
              <div className="lg:col-span-9 xl:col-span-10">
                <p className="aud-big text-h1 text-ink max-w-[18ch] text-balance">{p.big}</p>
                <p className="aud-sub mt-3 sm:mt-4 text-h3 font-normal text-muted max-w-[60ch] text-balance">
                  {p.sub}
                </p>
              </div>
            </li>
          ))}
        </ol>

        {/* Строка-переход к продукту */}
        <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-x-8">
          <p className="aud-bridge lg:col-start-4 xl:col-start-3 lg:col-span-9 xl:col-span-8 text-h3 text-ink max-w-[50ch]">
            {t.bridge}
          </p>
        </div>

        {/* Низ: кому подходит / кому нет — двумя короткими колонками */}
        <div className="mt-10 sm:mt-12">
          <p className={`${LABEL} text-muted`}>{t.eyebrow}</p>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-12 xl:gap-x-20 gap-y-6">
            {([[t.fitLabel, t.fit], [t.unfitLabel, t.unfit]] as const).map(([label, text]) => (
              <div key={label}>
                <h2 className="text-body font-medium text-ink leading-snug">{label}</h2>
                <p className="mt-2 text-small text-muted leading-relaxed max-w-[76ch]">{text}</p>
              </div>
            ))}
          </div>

          {/* Сегменты — одной строкой: ссылка растянута на всю ячейку
              (stretched-link), в <a> только название — доля слов в ссылках
              на странице не растёт. На /en ссылок нет: страницы не переведены. */}
          <ul className="mt-6 sm:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-5">
            {t.segments.map((seg) => (
              <li key={seg.sector} className="aud-seg relative border-t border-divider pt-4">
                <span className={`block ${LABEL} text-muted`}>{seg.chip}</span>
                {!isEn && seg.href ? (
                  <NavLink
                    href={seg.href}
                    className="stretched-link mt-1.5 inline-block text-body font-medium text-ink leading-snug no-underline"
                    onClick={() => track('audience_tile_click', { sector: seg.sector })}
                  >
                    {/* Неразрывный пробел: иначе стрелка на телефоне уезжает сиротой на свою строку. */}
                    {seg.title}{'\u00a0'}<span aria-hidden className="aud-arrow">→</span>
                  </NavLink>
                ) : (
                  <span className="mt-1.5 inline-block text-body font-medium text-ink leading-snug">{seg.title}</span>
                )}
              </li>
            ))}
          </ul>

          {!isEn && t.regions.length > 0 && (
            <p className="mt-5 sm:mt-6 text-small text-muted leading-relaxed">
              {t.regionsLead}{' '}
              {t.regions.map((r, i) => (
                <span key={r.href}>
                  {i > 0 && <span aria-hidden> · </span>}
                  <NavLink href={r.href} className="aud-region whitespace-nowrap">
                    {r.label}
                  </NavLink>
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
